import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Keyboard, Linking, StyleSheet } from 'react-native';
import i18n from 'i18n-js';

import { connect } from 'react-redux';
import { parse } from 'search-params';
import { Camera } from 'react-native-vision-camera';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';

import { scale, width } from '@components/frame';
import { isPhone, Alert, getSecureValue, setSecureValue, getSelectedCameraDevice, savePasswordToiCloud } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import EditableLine from '@components/common/editableLine';
import Loading, { SmallLoading } from '@components/loading';

import { userLogin } from '@reducers/user';
import { getAuthInfo, clear, updateWaitForNewAccount } from '@reducers/user';
import { authenticateDevice } from '@reducers/device';
import { closeAnonymSignalR, anonymSignalR } from '@reducers/signalR';
import { modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const RenderCamera = ({ authenticateDevice, closeAnonymSignalR, anonymSignalR, updateWaitForNewAccount, setLoading, C, loading }) => {
  const [isCameraWorking, setIsCameraWorking] = useState(true);
  const [localLoading, setLocalLoading] = useState(true);
  const scanned = useRef(false);
  const [device, setDevice] = useState(null);

  const validateCameraPermission = async (status) => {
    switch (status) {
      case 'not-determined': {
        const newCameraPermission = await Camera.requestCameraPermission();
        return await validateCameraPermission(newCameraPermission);
      }
      case 'granted': {
        const selectedDevice = await getSelectedCameraDevice();

        setDevice(selectedDevice);
        setIsCameraWorking(true);
        break;
      }
      default: {
        setIsCameraWorking(false);
        Linking.openSettings();
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const status = Camera.getCameraPermissionStatus();
        await validateCameraPermission(status);
        setLocalLoading(false);
      } catch (e) {
        console.log(e, 'error');
      }
    })();
  }, []);

  const invalidQr = () => Alert({ title: i18n.t('Wrong QR code') });

  const handleBarCodeScanned = async (code) => {
    let token = null;

    try {
      const data = code.value;
      setLoading(true);
      scanned.current = true;

      updateWaitForNewAccount(true);
      let info = data;

      if (!info) {
        return invalidQr();
      }

      const isQRLink = info.indexOf('/qr?') > 0;
      if (isQRLink) {
        const { addDevice } = parse(info);

        if (addDevice) {
          info = addDevice;
        }
      }

      const obj = JSON.parse(info);
      if (!obj || !obj.authenticationCode) {
        setLoading(false);
        scanned.current = false;

        return invalidQr();
      }

      await closeAnonymSignalR();

      setSecureValue('env', obj.env);

      token = getSecureValue('token');
      setSecureValue('token', null);

      await anonymSignalR();

      await authenticateDevice({
        authenticationCode: obj.authenticationCode,
      });
      return;
    } catch (e) {
      if (token) {
        setSecureValue('token', token);
      }
      setLoading(false);
      scanned.current = false;

      invalidQr();
    }
  };

  const qrCodeWidth = isPhone ? width - scale(30) : scale(450);

  if (localLoading) {
    return <Loading />;
  }

  return isCameraWorking ? (
    <View
      style={{
        width: qrCodeWidth,
        height: scale(450),
        marginLeft: scale(15),
        borderRadius: scale(10),
        overflow: 'hidden',
      }}
    >
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          position: 'absolute',
        }}
      >
        <SmallLoading />
      </View>
      <Camera
        device={device}
        style={{ width: qrCodeWidth, height: scale(450) }}
        codeScanner={{
          codeTypes: ['qr'],
          onCodeScanned: (codes) => {
            if (scanned.current || loading) {
            } else {
              handleBarCodeScanned(codes[0]);
            }
          },
        }}
        isActive={true}
      />
    </View>
  ) : (
    <View style={{ width: '100%', marginTop: scale(50), justifyContent: 'center', alignItems: 'center', alignContent: 'center' }}>
      <Text style={[font['Body'], { textAlign: 'center', color: C('Label/Dark/Primary') }]}>{i18n.t('No camera access')}</Text>
    </View>
  );
};
const Frame = (props) => {
  const { C, updateData, data = {}, inputs, submit, hide, subHeader, mode, setMode, loading, isError } = props;

  const isInput = mode === 'inputs';

  const wrapperStyle = isPhone
    ? {
        borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
        borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
      }
    : {
        borderRadius: scale(SCREEN_BORDER_RADIUS),
      };

  return (
    <View
      style={[
        wrapperStyle,
        isPhone
          ? {
              width: '100%',
              flex: 1,
            }
          : {
              paddingBottom: scale(17),
            },
        !isInput &&
          !isPhone && {
            height: scale(550),
          },
      ]}
    >
      <View
        style={[
          wrapperStyle,
          {
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'absolute',
            zIndex: loading ? 1000 : 0,
            opacity: loading ? 1 : 0,
          },
        ]}
      >
        <SmallLoading />
      </View>
      <View style={[wrapperStyle, { zIndex: loading ? 0 : 1000 }]}>
        <View
          style={{
            width: '100%',
            flexDirection: 'row',
            justifyContent: 'space-between',
            zIndex: 1000,
            height: scale(54),
          }}
        >
          <TouchableElement onPress={isInput ? hide : () => setMode('inputs')}>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { padding: scale(15) }]}>
                <Text style={[{ color: C(pressHoverColor('System/Light/Blue')) }, font.Body]}>{i18n.t('Cancel')}</Text>
              </View>
            )}
          </TouchableElement>
          {isInput && (
            <TouchableElement onPress={submit}>
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15) }]}>
                  <Text style={[{ color: C(pressHoverColor('Link/Light/Primary')) }, font.Body]}>{i18n.t('Add')}</Text>
                </View>
              )}
            </TouchableElement>
          )}
        </View>
        <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
          <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>
            {i18n.t(isInput ? 'New Account' : 'Scan QR Code')}
          </Text>
        </View>
        <View style={{ width: '100%' }}>
          <Text style={[{ color: C('Label/Light/Tertiary'), width: '100%', paddingLeft: scale(30), marginBottom: scale(5) }, font.Footnote]}>
            {subHeader}
          </Text>
        </View>
        {isInput ? (
          <View
            style={[
              { width: '100%' },
              !isPhone && {
                paddingHorizontal: scale(15),
              },
            ]}
          >
            <TouchableElement
              onPress={() => {
                setMode('code');
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      width: '100%',
                      height: scale(42),
                      backgroundColor: C(pressHoverColor(modalFieldBg)),
                      justifyContent: 'center',
                      paddingHorizontal: scale(15),
                      marginBottom: scale(30),
                    },
                    isPhone
                      ? {
                          borderColor: C('Hairline / Light / No trans'),
                          borderTopWidth: StyleSheet.hairlineWidth,
                          borderBottomWidth: StyleSheet.hairlineWidth,
                          alignItems: 'flex-start',
                        }
                      : {
                          alignItems: 'center',
                          borderRadius: scale(10),
                        },
                  ]}
                >
                  <Text style={[font['Body'], { color: C(pressHoverColor('Link/Dark/Primary')) }]}>{i18n.t('Scan QR Code')}</Text>
                </View>
              )}
            </TouchableElement>
            {inputs.map((item, index) => (
              <EditableLine
                key={item.key}
                {...{
                  item: {
                    value: data[item.key],
                    onChange: (v) => updateData(item.key, v),
                    ...item,
                  },
                  bg: modalFieldBg,
                  index,
                  array: inputs,
                  C,
                  bottomBorderBox: isPhone,
                  topBorderBox: isPhone,
                  topBorder: index !== 0,
                  roundTop: !isPhone && index === 0,
                  roundBottom: !isPhone && index === inputs.length - 1,
                }}
              />
            ))}
            {isError && (
              <View style={{ marginTop: scale(8), paddingHorizontal: scale(15) }}>
                <Text style={[font['Button 3'], { color: C('Label/Light/Error') }]}>
                  {i18n.t('Incorrect password. Please try again or you can reset your password.')}
                </Text>
              </View>
            )}
          </View>
        ) : (
          <RenderCamera {...props} />
        )}
      </View>
    </View>
  );
};

const NewAccount = (props) => {
  const {
    hide,
    userLogin,
    setAccounts,
    accounts,
    clear,
    getAuthInfo,
    selectNewUser,
    user: { waitForNewAccount },
  } = props;

  const [data, setData] = useState({});
  const [mode, setMode] = useState('inputs');
  const [loading, setLoading] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isError, setIsError] = useState(false);

  const inputs = [
    {
      key: 'email',
      placeholder: 'Email',
      noArrow: true,
      isLable: false,
    },
    {
      key: 'password',
      placeholder: 'Password',
      noArrow: true,
      isLable: false,
      secure: true,
    },
  ];

  useEffect(() => {
    return async () => {
      setData({});
      setLoading(false);
      setMode('inputs');
      Keyboard.dismiss();
    };
  }, []);

  useEffect(() => {
    if (waitForNewAccount) {
      setIsWaiting(true);
    }
    if (!waitForNewAccount && isWaiting) {
      (async () => {
        setIsWaiting(false);
        const accounts = getSecureValue('accounts');

        setAccounts(JSON.parse(accounts));
        hide();
      })();
    }
  }, [waitForNewAccount]);

  const submit = async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    const callback = () => {
      hide();
    };

    const exist = accounts.find((a) => a?.userInfo?.emailAddress === data.email);

    if (exist) {
      setLoading(false);
      return Alert({ title: i18n.t('This account already exist') });
    }

    const result = await userLogin({ email: data.email, pass: data.password, callback });

    if (result && result.access_token) {
      await savePasswordToiCloud('login.smarfac.com', data.email, data.password);
      await clear();
      setData({});
      const newUser = await getAuthInfo(true);
      const token = getSecureValue('token');
      const expiresIn = getSecureValue('expiresIn');
      const refreshToken = getSecureValue('refreshToken');
      const deviceCode = getSecureValue('deviceCode');
      const env = getSecureValue('env');
      const role = getSecureValue('role');
      const email = getSecureValue('email');

      const currentAccounts = [
        ...accounts,
        {
          userInfo: newUser,
          token,
          expiresIn,
          refreshToken,
          deviceCode,
          env,
          role,
          email,
        },
      ];

      setSecureValue('accounts', JSON.stringify(currentAccounts));
      setAccounts(currentAccounts);

      await selectNewUser();
    } else {
      setIsError(true);
    }

    setLoading(false);
  };

  const updateData = (key, v) => {
    const newData = {
      ...data,
      [key]: v,
    };
    setData(newData);
    setIsError(false);
  };

  const itemProps = {
    ...props,
    updateData,
    data,
    inputs,
    submit,
    hide,
    mode,
    setMode,
    loading,
    setLoading,
    isError,
  };

  return <Frame {...itemProps} />;
};
export default connect(({ user }) => ({ user }), {
  userLogin,
  clear,
  getAuthInfo,
  authenticateDevice,
  closeAnonymSignalR,
  anonymSignalR,
  updateWaitForNewAccount,
})(ColorWrapper(NewAccount));
